import Box from "@mui/material/Box";
import { ChangeEvent, ClipboardEvent, useState } from "react";
import { fontStretch } from "src/design/typography";

export interface CodeBoxesProps {
  length?: number;
  value: string;
  onChange: (next: string) => void;
  "aria-label": string;
  inputMode?: "numeric" | "text";
  uppercase?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  onComplete?: (value: string) => void;
}

export const CodeBoxes = ({
  length = 6,
  value,
  onChange,
  "aria-label": ariaLabel,
  inputMode = "text",
  uppercase = true,
  autoFocus,
  disabled,
  onComplete,
}: CodeBoxesProps) => {
  const [focused, setFocused] = useState(false);

  const commit = (raw: string) => {
    let next = raw.replace(/[^a-zA-Z0-9]/g, "");
    if (uppercase) next = next.toUpperCase();
    next = next.slice(0, length);
    onChange(next);
    if (next.length === length && next !== value) onComplete?.(next);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    commit(event.target.value);

  // maxLength truncates pasted text before it is sanitised, so strip symbols first.
  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData
      .getData("text")
      .replace(/[^a-zA-Z0-9]/g, "");
    const { selectionStart, selectionEnd } = event.currentTarget;
    const start = selectionStart ?? value.length;
    const end = selectionEnd ?? value.length;
    commit(value.slice(0, start) + pasted + value.slice(end));
  };

  const active = Math.min(value.length, length - 1);

  return (
    <Box sx={{ position: "relative", display: "block", width: "100%" }}>
      <Box
        component="input"
        type="text"
        value={value}
        onChange={handleChange}
        onPaste={handlePaste}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        maxLength={length}
        inputMode={inputMode}
        autoComplete="one-time-code"
        autoFocus={autoFocus}
        disabled={disabled}
        aria-label={ariaLabel}
        spellCheck={false}
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          opacity: 0,
          border: 0,
          padding: 0,
          cursor: disabled ? "default" : "text",
        }}
      />
      <Box
        aria-hidden
        sx={{
          display: "grid",
          gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))`,
          gap: 1,
        }}
      >
        {Array.from({ length }, (_, index) => (
          <Box
            key={index}
            data-testid="code-box"
            sx={(theme) => ({
              // Design: 72 px boxes, 16 px radius, 2 px border, condensed 32 px numerals.
              height: { xs: 60, sm: 72 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "16px",
              border: `2px solid ${
                focused && !disabled && index === active
                  ? theme.palette.primary.main
                  : theme.palette.inputBorder
              }`,
              backgroundColor: theme.palette.inputBg,
              color: theme.palette.text.primary,
              fontSize: { xs: 26, sm: 32 },
              fontWeight: 800,
              fontStretch: fontStretch.condensed,
              opacity: disabled ? 0.6 : 1,
            })}
          >
            {value[index] ?? ""}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default CodeBoxes;
